// @spec SHL-STR-048
'use client';

import * as React from 'react';

export type TextScale = '100' | '110' | '125' | '150' | '175' | '200';

const SCALE_CLASSES = [
  'font-scale-100',
  'font-scale-110',
  'font-scale-125',
  'font-scale-150',
  'font-scale-175',
  'font-scale-200',
] as const;

interface TextScaleProviderProps {
  children: React.ReactNode;
  defaultScale?: TextScale;
  storageKey?: string;
}

export interface TextScaleContextValue {
  scale: TextScale;
  setScale: (scale: TextScale) => void;
}

export const TextScaleContext = React.createContext<TextScaleContextValue | undefined>(undefined);

/**
 * TextScaleProvider component for managing text size preference (accessibility).
 *
 * Features:
 * - Persists text scale preference to localStorage
 * - Supports 6 scale levels: 100%, 110%, 125%, 150%, 175%, 200%
 * - Applies appropriate class to document root for CSS variable override
 * - WCAG 2.1 AA compliant: allows text resizing up to 200%
 *
 * @example
 * <TextScaleProvider defaultScale="100" storageKey="quantifier-text-scale">
 *   <App />
 * </TextScaleProvider>
 */
export function TextScaleProvider({
  children,
  defaultScale = '100',
  storageKey = 'quantifier-text-scale',
}: TextScaleProviderProps) {
  const [scale, setScaleState] = React.useState<TextScale>(() => {
    if (typeof window === 'undefined') {
      return defaultScale;
    }
    const stored = localStorage.getItem(storageKey);
    // Validate stored value is a valid scale
    if (stored && ['100', '110', '125', '150', '175', '200'].includes(stored)) {
      return stored as TextScale;
    }
    return defaultScale;
  });

  // Apply scale class to html element
  React.useEffect(() => {
    const root = window.document.documentElement;

    // Remove all scale classes
    SCALE_CLASSES.forEach((cls) => {
      root.classList.remove(cls);
    });

    // Add current scale class
    root.classList.add(`font-scale-${scale}`);
  }, [scale]);

  const setScale = React.useCallback(
    (newScale: TextScale) => {
      localStorage.setItem(storageKey, newScale);
      setScaleState(newScale);
    },
    [storageKey]
  );

  const value = React.useMemo(
    () => ({
      scale,
      setScale,
    }),
    [scale, setScale]
  );

  return <TextScaleContext.Provider value={value}>{children}</TextScaleContext.Provider>;
}
